import {
  useCallback,
  useEffect,
  useRef,
  type MouseEvent as ReactMouseEvent,
  type PointerEvent as ReactPointerEvent,
} from 'react'

const HOLD_MS = 500

/** A finger that travels further than this is scrolling, not holding. */
const MOVE_TOLERANCE_PX = 10

export interface LongPressHandlers {
  onPointerDown: (event: ReactPointerEvent<HTMLElement>) => void
  onPointerMove: (event: ReactPointerEvent<HTMLElement>) => void
  onPointerUp: () => void
  onPointerCancel: () => void
  onClickCapture: (event: ReactMouseEvent) => void
  onContextMenu: (event: ReactMouseEvent) => void
}

/**
 * Hold a row to enter selection mode.
 *
 * Touch only: a mouse has a right-click and a visible "Select" button, and
 * making a long left-click do something would be a trap for anyone who pauses
 * mid-press. Once the hold fires, the click that follows the finger lifting is
 * swallowed in the capture phase so the row does not also start playing.
 */
export function useLongPress(onLongPress?: () => void): LongPressHandlers {
  const callback = useRef(onLongPress)
  const timer = useRef<number | null>(null)
  const origin = useRef<{ x: number; y: number } | null>(null)
  const fired = useRef(false)

  useEffect(() => {
    callback.current = onLongPress
  }, [onLongPress])

  const stop = useCallback(() => {
    if (timer.current !== null) {
      window.clearTimeout(timer.current)
      timer.current = null
    }
    origin.current = null
  }, [])

  useEffect(() => stop, [stop])

  const onPointerDown = useCallback(
    (event: ReactPointerEvent<HTMLElement>) => {
      fired.current = false
      if (event.pointerType !== 'touch' || !callback.current) return

      // Only the row's own button starts a hold. The favourite and overflow
      // buttons are separate controls, and hijacking a hold on one of those
      // would swallow the tap the user meant to make.
      const pressed = event.target instanceof Element ? event.target.closest('button') : null
      if (pressed !== null && pressed !== event.currentTarget.querySelector('button')) return

      origin.current = { x: event.clientX, y: event.clientY }
      timer.current = window.setTimeout(() => {
        timer.current = null
        fired.current = true
        callback.current?.()
      }, HOLD_MS)
    },
    [],
  )

  const onPointerMove = useCallback(
    (event: ReactPointerEvent<HTMLElement>) => {
      const start = origin.current
      if (!start || timer.current === null) return
      const travelled = Math.hypot(event.clientX - start.x, event.clientY - start.y)
      if (travelled > MOVE_TOLERANCE_PX) stop()
    },
    [stop],
  )

  const onClickCapture = useCallback((event: ReactMouseEvent) => {
    if (!fired.current) return
    fired.current = false
    event.preventDefault()
    event.stopPropagation()
  }, [])

  const onContextMenu = useCallback((event: ReactMouseEvent) => {
    // Some touch browsers raise the callout menu at the same moment the hold
    // fires; the selection is the answer to that gesture here.
    if (fired.current) event.preventDefault()
  }, [])

  return {
    onPointerDown,
    onPointerMove,
    onPointerUp: stop,
    onPointerCancel: stop,
    onClickCapture,
    onContextMenu,
  }
}
